
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Loader2, Building2, Globe2, EuroIcon } from "lucide-react";
import { formatCurrency } from "@/utils/dateFormatters";
import { useTranslation } from "react-i18next";

interface CPVCode {
  CPV: number;
  Beskrivelse: string;
}

export const PreferencesSummary = ({ profile }: { profile: any }) => {
  const { t } = useTranslation("profile");
  
  const { data: cpvDescriptions, isLoading } = useQuery({
    queryKey: ["cpv-descriptions", profile.preferred_industries],
    queryFn: async () => {
      if (!profile.preferred_industries?.length) return [];
      
      const { data, error } = await supabase
        .from("CPV Table")
        .select("CPV, Beskrivelse")
        .in("CPV", profile.preferred_industries.map(Number));

      if (error) throw error;
      return data as CPVCode[];
    },
    enabled: !!profile.preferred_industries?.length,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const formatPriceRange = () => {
    if (!profile.price_filter_enabled) {
      return <span className="text-gray-500">{t("priceRange.noFiltering")}</span>;
    }
    
    const currency = profile.price_filter_currency || "EUR";
    
    if (profile.min_price_range || profile.max_price_range) {
      return (
        <span>
          {profile.min_price_range ? formatCurrency(profile.min_price_range, currency) : '0'} 
          {' - '}
          {profile.max_price_range ? formatCurrency(profile.max_price_range, currency) : '∞'}
        </span>
      );
    } else {
      return <span className="text-gray-500">{t("priceRange.noLimits")} ({currency})</span>;
    }
  };

  return (
    <Card className="p-4 mb-6 bg-primary/5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="flex items-start gap-3">
          <Building2 className="h-5 w-5 text-primary mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-gray-700">{t("cpvCodes.title")}</h3>
            <div className="mt-1 text-sm text-gray-600">
              {cpvDescriptions?.length ? (
                <span className="line-clamp-2">
                  {cpvDescriptions.map(cpv => cpv.Beskrivelse).join(', ')}
                </span>
              ) : (
                <span className="text-gray-500">{t("cpvCodes.noneSelected")}</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <Globe2 className="h-5 w-5 text-primary mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-gray-700">{t("countries.title")}</h3>
            <div className="mt-1 text-sm text-gray-600">
              {profile.preferred_countries?.length ? (
                <span className="line-clamp-2">
                  {profile.preferred_countries.join(', ')}
                </span>
              ) : (
                <span className="text-gray-500">{t("countries.noneSelected")}</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <EuroIcon className="h-5 w-5 text-primary mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-gray-700">{t("priceRange.title")}</h3>
            <div className="mt-1 text-sm text-gray-600">
              {formatPriceRange()}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};
